import React, {useEffect, useRef, useState} from 'react';
import styled from "styled-components";
import CameraLive from "./CameraLive";
import Canvas from "../canvas/CanvasLines";
import PoseDetectionTest from "../dialogs/PoseDetectionTest";
import Icn from "../Icn";
import {colors} from "../../maps/colors";
import PoseBorderForCamera from "./PoseBorderForCamera";
import {api} from '../../maps/data.js';

const WrapperSvgStyled = styled.div`
    position: relative;
`;

const EnlargeStyled = styled.div`
    position: absolute;
    top: 1px;
    right: 1px;
    background: deeppink;
`;

const CameraPoseDetection = ({disableControls, camera, poseDetection, topCameraBorder, field}) => {
    const [height, s_height] = useState(0);
    const [width, s_width] = useState(0);
    const [showTest, s_showTest] = useState(false);

    const {streamFullRtspUrl, pt, z, rotate} = camera;

    const stage = useRef(null);

    const last4Chars = str => {
        if (str) {
            const last4CharsVal = str.toString().slice(str.toString().length - 4);
            return last4CharsVal;
        }else {
            return 'no id'
        }
    };

    useEffect(() => {
        if (stage.current && stage.current.offsetWidth) {
            s_height(stage.current.offsetHeight);
            s_width(stage.current.offsetWidth);
        }
    }, [stage.current]);


    const score = poseDetection?.pose?.[0]?.score;

    const setXY = (startList, endList = [], color = '#fff') => {
        return {
            startX: width * startList[0] / 100,
            startY: height * startList[1] / 100,
            endX: width * endList[0] / 100,
            endY: height * endList[1] / 100,
            strokeStyle: color,
            lineWidth: 2
        };
    };

    const lines = [];
    const names = [];

    // from shoudler - elbow  - #019811
    // from elbow - wrist  - #34e500

    // from hip - knee  - #fbaa01
    // from knee - ankle  - #fdd546

    poseDetection?.pose?.forEach(poseData => {
        const xMax = poseData.boundingBoxPr.xMax;
        const xMin = poseData.boundingBoxPr.xMin;
        const yMax = poseData.boundingBoxPr.yMax;
        const yMin = poseData.boundingBoxPr.yMin;


        const left_shoulder = poseData.left_shoulder;
        const right_shoulder = poseData.right_shoulder;
        const left_elbow = poseData.left_elbow;
        const right_elbow = poseData.right_elbow;
        const left_wrist = poseData.left_wrist;
        const right_wrist = poseData.right_wrist;


        const nose = poseData.nose;
        const left_eye = poseData.left_eye;
        const right_eye = poseData.right_eye;
        const left_ear = poseData.left_ear;
        const right_ear = poseData.right_ear;


        const left_hip = poseData.left_hip;
        const right_hip = poseData.right_hip;
        const left_knee = poseData.left_knee;
        const right_knee = poseData.right_knee;
        const left_ankle = poseData.left_ankle;
        const right_ankle = poseData.right_ankle;


        // upper
        if (left_shoulder && left_elbow) {
            lines.push(setXY(left_shoulder, left_elbow, colors.BLUE));
        }
        if (right_shoulder && right_elbow) {
            lines.push(setXY(right_shoulder, right_elbow, colors.BLUE));
        }
        if (left_elbow && left_wrist) {
            lines.push(setXY(left_elbow, left_wrist, colors.BLUE));
        }
        if (right_elbow && right_wrist) {
            lines.push(setXY(right_elbow, right_wrist, colors.BLUE));
        }
        if (left_shoulder && right_shoulder) {
            lines.push(setXY(left_shoulder, right_shoulder, colors.BLUE));
        }

        // lower
        if (left_hip && left_knee) {
            lines.push(setXY(left_hip, left_knee, colors.ORANGE));
        }
        if (right_hip && right_knee) {
            lines.push(setXY(right_hip, right_knee, colors.ORANGE));
        }
        if (left_knee && left_ankle) {
            lines.push(setXY(left_knee, left_ankle, colors.ORANGE));
        }
        if (right_knee && right_ankle) {
            lines.push(setXY(right_knee, right_ankle, colors.ORANGE));
        }
        if (left_hip && right_hip) {
            lines.push(setXY(left_hip, right_hip, colors.ORANGE));
        }

        // body
        if (left_hip && left_shoulder) {
            lines.push(setXY(left_hip, left_shoulder, colors.PINK));
        }
        if (right_hip && right_shoulder) {
            lines.push(setXY(right_hip, right_shoulder, colors.PINK));
        }

        const name = poseData.name;
        const trackId = last4Chars(poseData?.trackId);
        if (trackId) {
            const noseData = setXY(poseData.nose);
            names.push({
                name: name,
                trackId: trackId,
                globalId: poseData.globalId,
                x: noseData.startX,
                y: noseData.startY
            });
        }
    });

    const calibrate = async () => {
        await api({
            type: 'calibratePoseFloor',
            data: {
                topCameraId: camera._id,
                fieldId: field._id,
                siteId: field.siteId
            }
        });
    };

    const removeCalibration = async () => {
        await api({
            type: 'calibratePoseFloor',
            data: {
                topCameraId: camera._id,
                fieldId: field._id,
                siteId: field.siteId,
                clear: true
            }
        });
    };

    return (
        <CameraLive
            disableControls={disableControls} pt={pt} z={z}
            camera={camera}
            rotate={rotate}
            rtspUrl={streamFullRtspUrl}
            resolution={`480x270`}>

            <PoseBorderForCamera
                border={topCameraBorder}
            />

            <div
                ref={stage}
                style={{
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    background: 'none'
                }}>
                <WrapperSvgStyled>
                    <Canvas
                        height={height}
                        width={width}
                        background={"rgba(000,000,000, 0.0)"}
                        lines={lines}
                        names={names}
                    />
                </WrapperSvgStyled>
            </div>
            {showTest && (
                <PoseDetectionTest
                    camera={camera}
                    poseDetection={poseDetection}
                    onClose={() => s_showTest(false)}
                />
            )}

            <div style={{position: 'absolute', bottom: 2, left: 2, zIndex: 999}}>
                <Icn onClick={() => calibrate()} style={{fontSize: 14, cursor: 'pointer'}}>calibrationIcon</Icn>
                <Icn onClick={() => removeCalibration()} style={{fontSize: 14, cursor: 'pointer'}}>delete</Icn>
            </div>

            <div>
                {
                    camera?.calibration?.map(calibration => <div style={{position: 'absolute', background:'white', width: 4, height: 4, borderRadius: 2, top: `${calibration.camera[1]}%`, left: `${calibration.camera[0]}%`}}/>)
                }
            </div>

            <EnlargeStyled>
                <Icn onClick={() => s_showTest(true)} style={{fontSize: 14, cursor: 'pointer'}}>bugReportIcon</Icn>
            </EnlargeStyled>
        </CameraLive>
    );
};

export default CameraPoseDetection;